/*
 * Copyright (c) 2021. Dave van Rijn Development
 */

import './style/optionsMenu.scss';

import React, {ReactElement, ReactNode} from 'react';
import classNames from "classnames";
import {hasHover} from "../util/controlUtil";
import {ElementPosition, MenuPlacement, OptionsMenuItem} from '../util/interfaces';
import AwesomeIcon from '../icon/awesomeIcon';
import {directTimeout} from "../util/componentUtil";
import {getScreenHeight, getScreenWidth} from '../util/responsiveUtil';

interface Props {
    onClickOption?: (option: React.ReactNode | OptionsMenuItem, evt: React.MouseEvent) => void;
    onClickOptionIcon?: Function;
    optionsComponent?: React.ReactElement;
    options: OptionsMenuItem[];
    placement: MenuPlacement;
    containerClass?: string;
    labelClassName?: string;
    label?: string;
    labelPosition: ElementPosition;
}

interface State {
    open: boolean;
}

export default class OptionsMenu extends React.Component<Props, State> {
    static defaultProps = {
        placement: MenuPlacement.BOTTOM_RIGHT,
        labelPosition: ElementPosition.RIGHT,
    };

    menuList: HTMLDivElement;
    container: HTMLDivElement;

    state: State = {
        open: false,
    };

    onClickOption = (option: OptionsMenuItem) => (evt: React.MouseEvent) => {
        if (option.disabled !== true) {
            evt.stopPropagation();
            const {onClickOption} = this.props;
            this.onHideMenu();
            if (option.onClick) option.onClick(evt);
            else if (onClickOption) onClickOption(option, evt);
        }
    };

    // noinspection JSUnusedGlobalSymbols
    onOpenExternal = () => {
        if (this.menuList && this.container) {
            const {right, bottom} = this.container.getBoundingClientRect(),
                coords = this.calculateMenuCoords(right, bottom);
            this.menuList.style.left = coords.x + 'px';
            this.menuList.style.top = coords.y + 'px';
            this.onOpenMenu();
        }
    };

    onClickOptionsIcon = (evt: React.MouseEvent) => {
        if (this.menuList) {
            const {onClickOptionIcon} = this.props,
                {clientX, clientY} = evt, {x, y} = this.calculateMenuCoords(clientX, clientY);
            this.menuList.style.left = x + 'px';
            this.menuList.style.top = y + 'px';
            this.onOpenMenu(true);
            if (onClickOptionIcon) onClickOptionIcon(evt);
        }
    };

    onHideMenu = () => {
        this.setState({open: false});
    };

    onOpenMenu = (toggle: boolean = false) => {
        if (toggle) this.setState({open: !this.state.open});
        else this.setState({open: true});
    };

    calculateMenuCoords = (clickX: number, clickY: number) => {
        let x = -999, y = -999;
        if (this.menuList) {
            const {placement} = this.props, screenWidth = getScreenWidth(), screenHeight = getScreenHeight(),
                menuRect = this.menuList.getBoundingClientRect();
            if (menuRect) {
                const {width, height} = menuRect;
                switch (placement) {
                    case MenuPlacement.BOTTOM_LEFT:
                        x = Math.max(clickX - width, 0);
                        y = clickY + height > screenHeight ? screenHeight - height : clickY;
                        break;
                    case MenuPlacement.BOTTOM_RIGHT:
                        x = clickX + width > screenWidth ? screenWidth - width : clickX;
                        y = clickY + height > screenHeight ? screenHeight - height : clickY;
                        break;
                    case MenuPlacement.TOP_LEFT:
                        x = Math.max(clickX - width, 0);
                        y = Math.max(clickY - height, 0);
                        break;
                    case MenuPlacement.TOP_RIGHT:
                        x = clickX + width > screenWidth ? screenWidth - width : clickX;
                        y = Math.max(clickY - height, 0);
                        break;
                }
            }
        }
        return {x, y};
    };

    attachListRef = (ref: HTMLDivElement) => {
        this.menuList = ref;
    };

    attachContainerRef = (ref: HTMLDivElement) => {
        this.container = ref;
    };

    clickListener = () => {
        if (!hasHover(this.container))
            this.onHideMenu();
    };

    addClickListener = () => {
        document.addEventListener('click', this.clickListener);
    };

    removeClickListener = () => {
        document.removeEventListener('click', this.clickListener);
    };

    renderOptionsElement = (): ReactElement => {
        const {optionsComponent} = this.props;
        if (optionsComponent) return React.cloneElement(optionsComponent, {onClick: this.onClickOptionsIcon});
        return <AwesomeIcon name='ellipsis-v' className='options-icon' onClick={this.onClickOptionsIcon}/>
    };

    renderLabel = (position: ElementPosition): ReactNode => {
        const {label, labelPosition, labelClassName} = this.props;
        if (position !== labelPosition || !label?.length) return null;
        return <label className={classNames('options-menu-label', labelPosition, labelClassName)}
                      onClick={this.onClickOptionsIcon}>{label}</label>
    }

    renderOptionsList = (): React.ReactElement => {
        const {options} = this.props, {open} = this.state;
        return (
            <div ref={this.attachListRef} className={classNames('menu-options-list', open && 'visible')}>
                {options.map((option, key) => (
                    <div className={classNames('menu-option', option.disabled === true && 'disabled')} key={key}
                         onClick={this.onClickOption(option)}>
                        {(option.icon && (!option.iconPlacement || option.iconPlacement === ElementPosition.LEFT)) &&
                        option.icon}
                        <label className='menu-option-label'>{option.label}</label>
                        {(option.icon && option.iconPlacement === ElementPosition.RIGHT) && option.icon}
                    </div>
                ))}
            </div>
        )
    };

    componentDidUpdate = (props: Props, prevState: State) => {
        const {open} = this.state;
        if (open && !prevState.open) directTimeout(this.addClickListener);
        else if (!open && prevState.open) directTimeout(this.removeClickListener);
    };

    componentWillUnmount = () => {
        this.removeClickListener();
    };

    render = () => {
        const {containerClass} = this.props;
        return (
            <div className={classNames('option-menu-container', containerClass)} ref={this.attachContainerRef}>
                {this.renderLabel(ElementPosition.LEFT)}
                {this.renderOptionsElement()}
                {this.renderLabel(ElementPosition.RIGHT)}
                {this.renderOptionsList()}
            </div>
        )
    };
};